import { FC, useEffect, useState } from 'react';
import { Button, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
	IQuizQuestion,
	IQuizQuestionTypes,
} from '../../../lib/interfaces/Quiz';
import LinearProgressWithLabel from '../../Simple/LinearProgress/LinearProgressWithLabel';
import QuizQuestion from './QuizQuestion';

type QuizWrapperProps = {
	questions: IQuizQuestion[];
	handleQuizCompleted: (isCompleted: boolean) => void;
};

const QuizWrapper: FC<QuizWrapperProps> = ({
	questions,
	handleQuizCompleted,
}) => {
	const { t } = useTranslation();
	const [currentQuestion, setCurrentQuestion] = useState<number>(0);
	const [showResults, setShowResults] = useState<boolean>(false);
	const [answers, setAnswers] = useState<any>({});
	const [checkMistakesMode, setCheckMistakesMode] = useState<boolean>(false);
	const [showQuiz, setShowQuiz] = useState<boolean>(true);

	const onNext = (answer: string | string[], questionId: string) => {
		if (!checkMistakesMode) {
			setAnswers({
				...answers,
				[questionId]: answer,
			});
		}
		if (currentQuestion === questions.length - 1) {
			// console.log('FINISH', answers);
			setShowResults(true);
		} else {
			setCurrentQuestion(currentQuestion + 1);
		}
	};

	const onPrev = () => {
		if (currentQuestion > 0) {
			setCurrentQuestion(currentQuestion - 1);
		}
	};

	const getResults = () => {
		// console.log('ANSWERS  : ', answers);
		const results: any = {};
		questions.forEach((question: IQuizQuestion) => {
			if (question.type === IQuizQuestionTypes.OPTIONS) {
				results[question.id] = question.answer?.includes(answers[question.id]?.[0]);
			}
			if (question.type === IQuizQuestionTypes.MULTIPLE_OPTIONS) {
				if (answers[question.id].length !== question.answer?.length) {
					results[question.id] = false;
				} else {
					const questionResults = question.answer?.map((answer: string) =>
						answers[question.id].includes(answer),
					);
					results[question.id] = !questionResults?.includes(false);
				}
			}
			if (question.type === IQuizQuestionTypes.EXACT_ANSWER) {
				results[question.id] = question.answer?.[0] === answers[question.id];
			}
		});

		// console.log(results);
		// setCheckedResults(results);

		const questionPoints = 100 / questions.length;
		const rightAnswers = Object.values(results).reduce(
			(aggregator: number, current) => (current ? aggregator + 1 : aggregator),
			0,
		);

		if (rightAnswers / questions.length >= 0.8) {
			handleQuizCompleted(true);
		}

		return {
			questionPoints,
			rightAnswers,
		};
	};

	const getPercentage = () => getResults().rightAnswers / questions.length;

	const checkMistakes = () => {
		setCheckMistakesMode(true);
		setCurrentQuestion(0);
		setShowResults(false);
	};

	const tryAgain = () => {
		setShowQuiz(false);
		setCheckMistakesMode(false);
		setCurrentQuestion(0);
		setShowResults(false);
	};

	useEffect(() => {
		setShowQuiz(true);
	}, [showQuiz]);

	return (
		<div>
			{showQuiz && (
				<div style={{ display: showResults ? 'none' : 'block' }}>
					{questions.map((question: IQuizQuestion, index: number) => (
						<QuizQuestion
							question={question}
							questionIndex={index}
							key={question.id}
							isCurrent={currentQuestion === index}
							onNext={onNext}
							onPrev={onPrev}
							checkMistakesMode={checkMistakesMode}
						/>
					))}
				</div>
			)}
			{showResults && (
				<Paper variant="outlined" elevation={0} style={{ padding: '20px' }}>
					<div>
						<Typography variant="h5" style={{ paddingBottom: '10px' }}>
							{getPercentage() < 0.5 && (
								<>
									{t('youCanDoBetter')} :) {t('giveTry')}!
								</>
							)}
							{getPercentage() >= 0.5 && getPercentage() < 0.8 && (
								<>{t('youCanDoBetterMore')}</>
							)}
							{getPercentage() >= 0.8 && <>{t('wellDone')}</>}
						</Typography>
						{getPercentage() >= 0.8 && (
							<Typography variant="body1" style={{ paddingBottom: '20px' }}>
								{t('completeQuizText')}
							</Typography>
						)}
					</div>
					<div style={{ paddingBottom: '10px' }}>
						<Typography>
							{t('rightAnswers')}: {getResults().rightAnswers}/{questions.length}
						</Typography>
						<div style={{ maxWidth: '300px' }}>
							<LinearProgressWithLabel
								variant="determinate"
								value={(getResults().rightAnswers / questions.length) * 100 || 0}
							/>
						</div>
						<div style={{ marginTop: '10px' }}>
							<Button
								variant="outlined"
								color="primary"
								style={{ marginRight: '15px' }}
								startIcon={<HelpOutlineIcon />}
								onClick={checkMistakes}
							>
								{t('checkMistakes')}
							</Button>
							<Button
								variant="outlined"
								color="primary"
								startIcon={<RestartAltIcon />}
								onClick={tryAgain}
							>
								{t('tryAgain')}
							</Button>
						</div>
					</div>
				</Paper>
			)}
		</div>
	);
};

export default QuizWrapper;
